import React from 'react';
import { Link } from 'react-router-dom';
import { SourceFunnel } from '../types';
import { fmtINR, STAGE_LABEL, STAGE_ORDER } from '../lib';
import { Section, Dash, Note, Th, td, tdBig, tr } from './PlatformBits';
import PlatformLogo, { PLATFORM, Platform } from './PlatformLogo';

/** Every ad platform's spend against what it bought, on one ladder: how many
 *  of its leads ever reached each stage (or further) and what a lead that far
 *  in cost. Reads the same first-touch cohort the platform pages do — a lead
 *  belongs to the platform its first touch names — so the rows here equal
 *  each page's strip; leads no platform claims get their own row, so the
 *  table sums to the strip's new leads. Spend arrives from each platform's
 *  own sync. */

export interface PlatformSpend {
  key: Platform;
  /** In ₹ (X converted at USD_INR, stated in the footnote). */
  spend: number;
  synced: boolean;
}

/** Fixed conversion for X's USD billing (market rate, Oct 2026). Shown on the
 *  page; change it here when it drifts — one number, not a rate service. */
export const USD_INR = 96;

// The stages worth pricing, closest to a visit first: cumulative, so a lead
// that reached `visited` counts under visit_scheduled and visit_intent too.
// Read from `reached` — how far a lead ever got — so one that was engaged and
// has since gone quiet or been disqualified still counts as engaged.
const PRICED = ['visited', 'visit_scheduled', 'visit_intent', 'engaged', 'contacted'];
const rank = (st: string) => { const i = STAGE_ORDER.indexOf(st); return i < 0 ? 99 : i; };

/** Which platform a first-touch source belongs to: a CTWA ad or a Meta UTM is
 *  Meta; otherwise the utm_source names it. */
export const platformOf = (s: Pick<SourceFunnel, 'source_kind' | 'source'>): PlatformSpend['key'] | null => {
  if (s.source_kind === 'ad') return 'meta';
  const src = (s.source ?? '').toLowerCase();
  if (['ig', 'fb', 'instagram', 'facebook', 'meta'].includes(src)) return 'meta';
  if (['x', 'twitter', 't.co', 'com.twitter.android'].includes(src)) return 'x';
  if (src === 'google') return 'google';
  if (src === 'linkedin') return 'linkedin';
  if (src === 'openai' || src === 'chatgpt') return 'chatgpt';
  return null;
};

const atLeast = (reached: Record<string, number>, st: string): number =>
  Object.entries(reached).reduce((a, [k, v]) => a + (rank(k) <= rank(st) ? v : 0), 0);

const sum = (mine: SourceFunnel[], field: 'stages' | 'reached') => {
  const out: Record<string, number> = {};
  mine.forEach((s) => Object.entries(s[field]).forEach(([k, v]) => { out[k] = (out[k] ?? 0) + v; }));
  return out;
};

/** `crmLeads`: leads born in Sell.do in the same window — expos, portals, IVR,
 *  bulk uploads. Core keeps them out of every cohort here (no ad money bought
 *  them, so they would only dilute the cost columns); the note names them so
 *  the table is read as our funnel, not as the whole book. */
const PlatformsCard: React.FC<{ platforms: PlatformSpend[]; sources: SourceFunnel[]; crmLeads: number }> = ({ platforms, sources, crmLeads }) => {
  const rows = platforms.map((p) => {
    const mine = sources.filter((s) => platformOf(s) === p.key);
    return { ...p, leads: mine.reduce((a, s) => a + s.new_leads, 0), stages: sum(mine, 'stages'), reached: sum(mine, 'reached') };
  });
  // Website, organic and no-source arrivals: no spend to price them against.
  const rest = sources.filter((s) => platformOf(s) === null);
  const other = { leads: rest.reduce((a, s) => a + s.new_leads, 0), stages: sum(rest, 'stages'), reached: sum(rest, 'reached') };
  const all = [...rows, other];
  const total = { spend: rows.reduce((a, r) => a + r.spend, 0), leads: all.reduce((a, r) => a + r.leads, 0) };
  // The visited column is the headline (§0): larger and green; the rest of
  // the ladder reads quieter as it moves away from the visit.
  const cell = (n: number, spend: number | null, head?: boolean) => (
    <td className={`${td} text-right`}>
      {n
        ? <>
            <span className={head ? 'font-semibold text-[15px] text-forest' : 'font-semibold'}>{n.toLocaleString('en-IN')}</span>
            {spend !== null && <div className="text-[11px] text-graphite mt-0.5">{fmtINR(spend / n)} each</div>}
          </>
        : <Dash />}
    </td>
  );
  const ladder = (stages: (st: string) => number, spend: number | null) =>
    PRICED.map((st) => <React.Fragment key={st}>{cell(stages(st), spend, st === 'visited')}</React.Fragment>);
  return (
    <Section title="Platforms" note="spend against how far its leads ever got — a lead counts at the furthest stage it reached and every stage before it">
      <table className="w-full min-w-[900px] border-collapse">
        <thead><tr>
          <Th left>Platform</Th><Th>Spend</Th><Th>New leads</Th>
          {PRICED.map((st) => <Th key={st} ours>{STAGE_LABEL[st]}+</Th>)}
          <Th>Disqualified</Th>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const idle = r.synced && r.spend === 0 && r.leads === 0;
            return (
              <tr key={r.key} className={`${tr} ${idle ? 'text-graphite' : ''}`}>
                <td className="px-3 py-3 whitespace-nowrap">
                  <Link to={PLATFORM[r.key].to} className="group inline-flex items-center gap-2.5 font-semibold text-charcoal hover:text-forest">
                    <PlatformLogo platform={r.key} className={`w-[18px] h-[18px] ${idle ? 'opacity-50' : ''}`} />
                    <span className="group-hover:underline underline-offset-2">{PLATFORM[r.key].label}</span>
                    <span aria-hidden className="text-graphite/50 group-hover:text-forest transition-colors">›</span>
                  </Link>
                  {!r.synced && <div className="text-[11px] text-brick mt-0.5 pl-[28px]">spend not loaded</div>}
                </td>
                <td className={`${tdBig} ${idle ? 'text-graphite font-normal' : ''}`}>{fmtINR(r.spend)}</td>
                {cell(r.leads, r.spend)}
                {ladder((st) => atLeast(r.reached, st), r.spend)}
                <td className={`${td} text-right text-graphite`}>{r.stages.disqualified ?? <Dash />}</td>
              </tr>
            );
          })}
          {other.leads > 0 && (
            <tr className={tr}>
              <td className="px-3 py-3 whitespace-nowrap text-graphite font-semibold">No platform
                <div className="text-[11px] font-normal mt-0.5">website, organic, no source</div>
              </td>
              <td className={`${td} text-right`}><Dash /></td>
              {cell(other.leads, null)}
              {ladder((st) => atLeast(other.reached, st), null)}
              <td className={`${td} text-right text-graphite`}>{other.stages.disqualified ?? <Dash />}</td>
            </tr>
          )}
          <tr className="border-t border-charcoal/20 align-top">
            <td className="px-3 py-3 font-semibold whitespace-nowrap">All leads</td>
            <td className={tdBig}>{fmtINR(total.spend)}</td>
            {cell(total.leads, total.spend)}
            {ladder((st) => all.reduce((a, r) => a + atLeast(r.reached, st), 0), total.spend)}
            <td className={`${td} text-right text-graphite`}>{all.reduce((a, r) => a + (r.stages.disqualified ?? 0), 0) || <Dash />}</td>
          </tr>
        </tbody>
      </table>
      <Note>
        Our funnel only. {crmLeads > 0
          ? <><span className="font-semibold text-charcoal">{crmLeads.toLocaleString('en-IN')}</span> more {crmLeads === 1 ? 'lead was' : 'leads were'} born in Sell.do this window (expos, portals, IVR, uploads) — </>
          : 'Leads born in Sell.do (expos, portals, IVR, uploads) '}no ad money bought them, so they sit in no row and no cost above.
        X is billed in USD, shown at ₹{USD_INR}/$. LinkedIn reports UTC days, so its day runs 05:30 → 05:30 IST.
      </Note>
    </Section>
  );
};

export default PlatformsCard;
